// Screen 1 · Sign-in — calm, low-friction. Errors are non-blaming (Design Brief).
//
// Two ways in: (1) "Sign in with Google" — admin/Lead only; shown only when VITE_GOOGLE_CLIENT_ID is
// configured. (2) email + passcode — the teachers' path AND the Lead's passcode fallback. Both end
// in the same place: a session cookie + navigate home.

import { api } from "../api";
import { setMe, type Me } from "../state";
import { navigate } from "../router";
import { isNetworkError } from "../offline";
import { t } from "../i18n";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

// Resolve a sign-in request to either the home screen or a calm inline error. Shared by both paths.
async function completeSignin(request: Promise<Me>, err: HTMLElement): Promise<void> {
  err.hidden = true;
  try {
    const m = await request;
    setMe(m);
    navigate("/");
  } catch (ex) {
    err.textContent = isNetworkError(ex) ? t("offline.signin") : (ex as Error).message;
    err.hidden = false;
  }
}

// Load Google Identity Services once, on demand. Resolves when window.google.accounts.id is ready.
function loadGoogleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as { google?: unknown }).google) return resolve();
    const existing = document.getElementById("gsi-client");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("gsi load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.id = "gsi-client";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("gsi load failed"));
    document.head.appendChild(s);
  });
}

export function renderSignin(app: HTMLElement): void {
  const googleBlock = GOOGLE_CLIENT_ID
    ? `
      <div id="gbtn" style="display:flex;justify-content:center;margin-bottom:12px"></div>
      <p class="hint" style="text-align:center;margin:0 0 4px">${t("signin.google_hint")}</p>
      <div class="or-divider" aria-hidden="true"
           style="display:flex;align-items:center;gap:10px;color:#9aa39b;font-size:13px;margin:14px 0">
        <span style="flex:1;height:1px;background:#dfe5df"></span>${t("signin.or")}<span style="flex:1;height:1px;background:#dfe5df"></span>
      </div>`
    : "";

  app.innerHTML = `
    <div class="topbar"><span></span></div>
    <span class="wordmark">Eden<i class="cross" aria-hidden="true"></i></span>
    <div class="eyebrow" style="margin:6px 0 var(--space-6)">English Department Engine and Network</div>
    <h1>${t("signin.title")}</h1>
    <p class="hint">${t("signin.hint")}</p>
    ${googleBlock}
    <form id="signin" novalidate>
      <label for="email">${t("signin.email")}</label>
      <input id="email" type="email" autocomplete="username" required />
      <label for="passcode">${t("signin.passcode")}</label>
      <input id="passcode" type="password" autocomplete="current-password" required />
      <div id="err" class="error" hidden></div>
      <div style="height:16px"></div>
      <button class="btn" type="submit">${t("signin.button")}</button>
    </form>`;

  const err = app.querySelector<HTMLElement>("#err")!;

  // Cold-reload while offline lands here (the session can't be confirmed offline). Say so
  // calmly up front, rather than letting a submit fail with a raw network error.
  if (!navigator.onLine) {
    err.textContent = t("offline.signin");
    err.hidden = false;
  }

  // ── Google sign-in (admin/Lead) — only when configured. Failure to load leaves passcode intact. ──
  if (GOOGLE_CLIENT_ID && navigator.onLine) {
    loadGoogleScript()
      .then(() => {
        const gid = (window as unknown as { google: { accounts: { id: GoogleId } } }).google.accounts.id;
        gid.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp: { credential: string }) =>
            completeSignin(
              api<Me>("/api/auth/google", { method: "POST", body: JSON.stringify({ credential: resp.credential }) }),
              err,
            ),
        });
        const slot = app.querySelector<HTMLElement>("#gbtn");
        // locale:"en" — the UI is English-only (2026-06-14); without this, Google's widget
        // localizes its own button text to the device locale (e.g. Khmer), breaking the language.
        if (slot) gid.renderButton(slot, { theme: "outline", size: "large", text: "signin_with", width: 280, locale: "en" });
      })
      .catch(() => {
        /* offline or blocked — the passcode form below still works */
      });
  }

  // ── Email + passcode (teachers + the Lead's fallback) ──
  app.querySelector<HTMLFormElement>("#signin")!.addEventListener("submit", (e) => {
    e.preventDefault();
    const email = app.querySelector<HTMLInputElement>("#email")!.value.trim();
    const passcode = app.querySelector<HTMLInputElement>("#passcode")!.value;
    void completeSignin(
      api<Me>("/api/auth/signin", { method: "POST", body: JSON.stringify({ email, passcode }) }),
      err,
    );
  });
}

// Minimal shape of the Google Identity Services API we use (the script provides the implementation).
interface GoogleId {
  initialize(config: { client_id: string; callback: (resp: { credential: string }) => void }): void;
  renderButton(
    parent: HTMLElement,
    options: { theme?: string; size?: string; text?: string; width?: number; locale?: string },
  ): void;
}
